import React, { useCallback, useState } from 'react';
import {
  ButtonProps,
  OutlineButton,
  PrimaryButton,
  Typography,
} from 'glints-aries/es/@next';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  getEmployerSupportHelpCenterRequestUrl,
  isEmployerSupportWorkingHours,
} from '../../common/helpers/employerSupportWorkingHours';
import { messages } from '../../common/hooks/useOpenChatSupport';
import { getConfig } from '../../config';
import { useZendeskVisibilityContext } from '../../modules/Zendesk/ZendeskVisibilityContext';

type ButtonType = 'primary' | 'outline';

type RenderCustomButtonProps = {
  loading?: boolean;
  onClick: () => void;
};

type Props = ButtonProps & {
  buttonType?: ButtonType;
  renderCustomButton?: (props: RenderCustomButtonProps) => JSX.Element;
};

const ContactSupportButton = (props?: Props) => {
  const { formatMessage } = useIntl();
  const { COUNTRY } = getConfig();
  const [loading, setLoading] = useState(false);
  const { openZendeskMessaging } = useZendeskVisibilityContext();

  const helpCenterRequestUrl = getEmployerSupportHelpCenterRequestUrl(COUNTRY);

  const handleContactSupport = useCallback(async () => {
    if (!isEmployerSupportWorkingHours()) {
      window.open(helpCenterRequestUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    const prefilledMessage = encodeURIComponent(
      formatMessage(messages.textHello)
    );

    setLoading(true);
    const isZendeskOpened = await openZendeskMessaging();
    setLoading(false);

    if (isZendeskOpened) {
      return;
    }

    window.open(
      `mailto:hi@glints.com?subject=${prefilledMessage}`,
      '_blank',
      'noopener,noreferrer'
    );
  }, [formatMessage, helpCenterRequestUrl, openZendeskMessaging]);

  if (props?.renderCustomButton) {
    return props.renderCustomButton({
      loading,
      onClick: handleContactSupport,
    });
  }

  const buttonText = (
    <Typography variant="button">
      <FormattedMessage
        id="interactive-contact-support"
        defaultMessage="Contact Support"
      />
    </Typography>
  );

  return (
    <Choose>
      <When condition={props?.buttonType === 'primary'}>
        <PrimaryButton
          {...props}
          loading={loading}
          onClick={handleContactSupport}
        >
          {buttonText}
        </PrimaryButton>
      </When>
      <Otherwise>
        <OutlineButton
          {...props}
          loading={loading}
          onClick={handleContactSupport}
        >
          {buttonText}
        </OutlineButton>
      </Otherwise>
    </Choose>
  );
};

export default ContactSupportButton;
